// Полный график динамики для s6 BiomarkerDetail (recharts).
// Source: docs/specs/sprint-5/stage-4-frontend.md §4.3 + RF-S5-S4-9 (ResponsiveContainer + custom Tooltip).

import {
  Line,
  LineChart,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { BiomarkerDetailPoint, TimelinePeriod } from '@/api/types';
import {
  type ChartDatum,
  computeDomain,
  fmtDate,
  formatRuDate,
  isQualitativeSeries,
  RANK_LABEL,
  shapeData,
} from '@/lib/chart-data';
import type { DisplayTier } from '@/lib/biomarkerStatus';
import { capitalizeFirst, formatNumber } from '@/lib/utils';

type Props = {
  points: BiomarkerDetailPoint[];
  normalMin: number | null;
  normalMax: number | null;
  unit: string;
  period: TimelinePeriod;
};

// Подпись оси Y: короткое число без float-хвостов. Целые показываем без
// дробной части (10 вместо 10.00), дробные — с двумя знаками.
function fmtYTick(v: number): string {
  return Number.isInteger(v) ? String(v) : v.toFixed(2);
}

// Цвет точки = ТОЛЬКО медицинский статус результата на эту дату (HEAL-16).
// Состояние «выбрана» передаётся размером и обводкой, не цветом.
//
// HEAL-35: ключ — дисплей-тир, а не clinical_status. Оранжевого «в норме, но не
// оптимум» здесь раньше не было вовсе, и график спорил с шапкой карточки.
// SVG-атрибут fill не понимает tailwind-классы, поэтому токены продублированы
// литералами — norm/attention/bad обязаны совпадать с tailwind.config.ts. Серый
// здесь gray-400, как у DOT_BG_BY_TIER (точка, а не полоска карточки).
export const STATUS_DOT_COLOR: Record<DisplayTier, string> = {
  norm: 'rgb(34 197 94)',
  attention: 'rgb(245 158 11)',
  bad: 'rgb(239 68 68)',
  'no-range': 'rgb(156 163 175)',
};

const DOT_STYLE = { r: 4, strokeWidth: 1.5 };
const ACTIVE_DOT_STYLE = { r: 6, strokeWidth: 2.5 };

export function BiomarkerChart({ points, normalMin, normalMax, unit, period }: Props) {
  if (points.length === 0) {
    return (
      <div className="flex h-60 items-center justify-center text-sm text-muted-foreground">
        Нет данных за выбранный период
      </div>
    );
  }

  const data = shapeData(points, period);
  const [domainMin, domainMax] = computeDomain(points, normalMin, normalMax);

  // HEAL-33: качественный ряд. По оси Y стоит ранг статуса (0 норма → 1 внимание →
  // 2 отклонение), поэтому подписи — словами: «1» на оси читалось бы как результат
  // анализа. Зелёная зона нормы тоже не рисуется — у ранга нет «коридора».
  const qualitative = isQualitativeSeries(data);

  return (
    <ResponsiveContainer width="100%" height={240}>
      <LineChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
        {!qualitative && normalMin !== null && normalMax !== null && (
          <ReferenceArea y1={normalMin} y2={normalMax} fill="rgba(34,197,94,0.08)" />
        )}
        <XAxis
          dataKey="x"
          tickFormatter={fmtDate(period)}
          tick={{ fontSize: 11, fill: 'rgb(107 114 128)' }}
          stroke="rgb(229 231 235)"
        />
        <YAxis
          domain={qualitative ? [0, 2] : [domainMin, domainMax]}
          ticks={qualitative ? [0, 1, 2] : undefined}
          tickFormatter={qualitative ? (v: number) => RANK_LABEL[v] ?? '' : fmtYTick}
          tick={{ fontSize: 11, fill: 'rgb(107 114 128)' }}
          stroke="rgb(229 231 235)"
          width={qualitative ? 78 : 44}
        />
        <Tooltip content={(p: TooltipPayloadProps) => renderTooltip(p, unit)} />
        <Line
          // Ступенька, а не сглаживание: между «Норма» и «Отклонение» нет
          // промежуточных состояний, которые кривая пририсовывает.
          type={qualitative ? 'stepAfter' : 'monotone'}
          dataKey="y"
          stroke="hsl(158 64% 35%)"
          strokeWidth={2}
          dot={renderDot}
          activeDot={renderActiveDot}
          isAnimationActive={false}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

// Recharts TooltipProps generic conflicts with our typed Tooltip content
// (NameType=string vs default unknown). Локальный narrowed shape — единственное
// поле, которое мы реально читаем, плюс safe cast'ы по runtime'у.
type TooltipPayloadProps = {
  active?: boolean;
  payload?: Array<{ payload?: unknown }>;
};

function renderTooltip(
  { active, payload }: TooltipPayloadProps,
  unit: string,
) {
  const entry = payload?.[0]?.payload;
  if (!active || !entry) return null;
  const d = entry as ChartDatum;
  return (
    <div className="rounded bg-surface p-2 text-xs shadow">
      <div>{formatRuDate(d.x)}</div>
      {/* HEAL-33: у качественного результата показываем слово. Ранг — внутренняя
          механика оценки, пользователю он ничего не говорит. */}
      {d.word ? (
        <div className="font-semibold">{capitalizeFirst(d.word)}</div>
      ) : (
        <div className="font-mono">
          {formatNumber(d.y)} {unit}
        </div>
      )}
      {d.labSource && <div className="text-muted-foreground">{d.labSource}</div>}
    </div>
  );
}

type DotProps = { cx?: number; cy?: number; payload?: ChartDatum; index?: number };

// Единая отрисовка точки: fill всегда из статуса, различается только геометрия.
// Recharts по умолчанию красит активную точку в цвет линии (зелёный бренд) —
// из-за этого красная точка «зеленела» при тапе, см. HEAL-16.
function renderStatusDot(
  { cx, cy, payload, index }: DotProps,
  { r, strokeWidth }: { r: number; strokeWidth: number },
  keyPrefix: string,
) {
  if (cx === undefined || cy === undefined || !payload) return <g key={`${keyPrefix}-${index}`} />;
  return (
    <circle
      key={`${keyPrefix}-${index}-${payload.uploadId}`}
      cx={cx}
      cy={cy}
      r={r}
      fill={STATUS_DOT_COLOR[payload.tier]}
      stroke="white"
      strokeWidth={strokeWidth}
    />
  );
}

function renderDot(props: DotProps) {
  return renderStatusDot(props, DOT_STYLE, 'dot');
}

function renderActiveDot(props: DotProps) {
  return renderStatusDot(props, ACTIVE_DOT_STYLE, 'active-dot');
}
